import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, type AvatarStyle, type User } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { sessionKeys } from '@/lib/session';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Avatar, Portrait } from '@/components/ui/Avatar';

/**
 * Who you are, at the top of your own account.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 *
 * A white box with a name and two lines of monospace under it, which read as a
 * database row rather than as a person's account — the same treatment the
 * screen gives a saved address. The first thing a buyer sees when they open
 * Account should be recognisably theirs.
 *
 * So: brand blue behind it, the face on the left, the name and both contacts
 * stacked beside it. Side by side rather than in a column, because a column of
 * four left-aligned strings is exactly what it looked like before.
 *
 * ── WHY BOTH CONTACTS ARE STILL SHOWN IN FULL ───────────────────────────────
 *
 * Unchanged from the version this replaces, and worth keeping deliberately.
 * These are the two places a sign-in code can reach this person, so seeing them
 * is how somebody notices a typo BEFORE it is the reason they cannot get back
 * into their account. Prettier would be to hide the email behind a chevron;
 * this is one of the places prettier is worse.
 *
 * ── AND WHY THE PORTRAITS ARE OFFERED, NOT ASSIGNED ─────────────────────────
 *
 * The picker shows three drawings and lets the buyer tap one. Nothing here or
 * on the server derives a portrait from somebody's name — see
 * `App\Enums\AvatarStyle`. Doing so would mean greeting a person, on the first
 * screen of their own account, with an assumption about them that a Nigerian
 * name gives no basis for in the first place.
 */
export function IdentityCard({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  const [picking, setPicking] = useState(false);

  return (
    <section className="mb-4 overflow-hidden rounded-card border border-line bg-white">
      {/* The band. Deep brand blue, so the white face and wordmark sit on it
          the way they do on the trust bar — one visual idea, used twice. */}
      <div className="relative bg-blue-deep px-4 pb-4 pt-4">
        {/* A soft wash rather than a flat block: the card is the first thing
            on the screen and a solid rectangle of blue is a lot of it. */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(120% 140% at 0% 0%, rgb(255 255 255 / 0.18) 0%, rgb(255 255 255 / 0) 60%)',
          }}
        />

        <div className="relative flex items-center gap-3.5">
          <AvatarButton user={user} onPick={() => setPicking((open) => !open)} />

          <div className="min-w-0 flex-1">
            <p className="m-0 truncate font-display text-[18px] font-bold tracking-[-0.015em] text-white">
              {user.name}
            </p>

            {user.phone ? (
              <p className="m-0 mt-1 flex items-center gap-1.5 text-[12.5px] text-white/80">
                <Icon name="phone" size={12} strokeWidth={2} className="shrink-0" />
                <span className="truncate font-mono">{user.phone}</span>
              </p>
            ) : null}

            {user.email ? (
              <p className="m-0 mt-0.5 flex items-start gap-1.5 text-[12.5px] leading-snug text-white/80">
                <Icon name="message" size={12} strokeWidth={2} className="mt-[3px] shrink-0" />
                {/* Wrapped, not truncated. Seeing the WHOLE address is the
                    point: this is where somebody spots the typo that would
                    otherwise be the reason a sign-in code never arrives. */}
                <span className="min-w-0 break-all font-mono">{user.email}</span>
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {picking ? <AvatarPicker user={user} onDone={() => setPicking(false)} /> : null}

      <div className="flex flex-wrap items-center gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => setPicking((open) => !open)}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-btn border border-line bg-white px-3.5 text-[13px] font-semibold text-ink"
        >
          <Icon name="camera" size={14} strokeWidth={1.9} />
          {user.avatar_url || user.avatar_style ? 'Change photo' : 'Add a photo'}
        </button>

        <Button variant="grey" size="sm" className="ml-auto" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
    </section>
  );
}

/** The face, and the camera badge that says it can be changed. */
function AvatarButton({ user, onPick }: { user: User; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-label="Change your picture"
      data-testid="avatar-button"
      className="relative grid h-14 w-14 shrink-0 place-items-center rounded-full"
    >
      <Avatar
        name={user.name}
        initials={user.initials}
        photoUrl={user.avatar_url}
        style={user.avatar_style}
        size={56}
      />

      <span className="absolute -bottom-0.5 -right-0.5 grid h-[19px] w-[19px] place-items-center rounded-full border-2 border-blue-deep bg-white text-blue">
        <Icon name="camera" size={10} strokeWidth={2.2} />
      </span>
    </button>
  );
}

const PORTRAITS: AvatarStyle[] = ['woman', 'man', 'neutral'];

/**
 * Upload one, or pick one.
 *
 * Opens inside the card rather than as a sheet: it is two short rows, and a
 * full-screen overlay for choosing a small picture is more ceremony than the
 * decision deserves.
 */
function AvatarPicker({ user, onDone }: { user: User; onDone: () => void }) {
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const save = (updated: User) => {
    queryClient.setQueryData(sessionKeys.me(), updated);
  };

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadAvatar(file),
    onSuccess: (updated) => {
      save(updated);
      onDone();
    },
  });

  const remove = useMutation({
    mutationFn: () => api.removeAvatar(),
    onSuccess: save,
  });

  const choose = useMutation({
    mutationFn: (style: AvatarStyle | null) => api.updateProfile({ avatar_style: style }),
    onSuccess: (result) => save(result.user),
  });

  const busy = upload.isPending || remove.isPending || choose.isPending;

  const error = [upload.error, remove.error, choose.error].find(
    (one): one is ApiError => one instanceof ApiError,
  );

  return (
    <div className="border-b border-line bg-paper px-4 py-3.5">
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];

          if (file) upload.mutate(file);

          // Cleared so picking the SAME file again still fires a change event.
          event.target.value = '';
        }}
      />

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => fileInput.current?.click()}
          className="inline-flex min-h-11 items-center gap-1.5 rounded-btn bg-blue px-3.5 text-[13px] font-bold text-white disabled:opacity-60"
        >
          <Icon name="camera" size={14} strokeWidth={1.9} />
          {upload.isPending ? 'Uploading…' : 'Upload a photo'}
        </button>

        {user.avatar_url ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => remove.mutate()}
            className="inline-flex min-h-11 items-center rounded-btn border border-line bg-white px-3.5 text-[13px] font-semibold text-muted"
          >
            Remove photo
          </button>
        ) : null}
      </div>

      {/* Offered, never assigned. Nothing anywhere guesses this from a name. */}
      <p className="m-0 mt-3 text-[12px] font-medium text-muted">Or pick a picture</p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {PORTRAITS.map((style) => {
          const chosen = !user.avatar_url && user.avatar_style === style;

          return (
            <button
              key={style}
              type="button"
              disabled={busy}
              aria-pressed={chosen}
              data-testid={`avatar-style-${style}`}
              onClick={() => choose.mutate(style)}
              className={cn(
                'grid h-11 w-11 place-items-center rounded-full ring-2 ring-offset-2 ring-offset-paper',
                chosen ? 'ring-blue' : 'ring-transparent hover:ring-line',
              )}
            >
              <Portrait style={style} size={40} />
            </button>
          );
        })}

        {/* Back to initials, which is where everybody starts. */}
        <button
          type="button"
          disabled={busy}
          aria-pressed={!user.avatar_url && !user.avatar_style}
          data-testid="avatar-style-initials"
          onClick={() => choose.mutate(null)}
          className={cn(
            'grid h-11 w-11 place-items-center rounded-full ring-2 ring-offset-2 ring-offset-paper',
            !user.avatar_url && !user.avatar_style
              ? 'ring-blue'
              : 'ring-transparent hover:ring-line',
          )}
        >
          <span className="grid h-10 w-10 place-items-center rounded-full bg-blue-deep font-display text-[14px] font-bold text-white">
            {user.initials}
          </span>
        </button>
      </div>

      {error ? (
        <p role="alert" className="m-0 mt-2.5 text-[12.5px] font-medium text-danger">
          {error.fieldErrors.file?.[0] ?? error.message}
        </p>
      ) : null}

      <button
        type="button"
        onClick={onDone}
        className="mt-3 min-h-11 bg-transparent p-0 text-[12.5px] font-bold text-muted"
      >
        Done
      </button>
    </div>
  );
}
